import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Palette, FloppyDisk, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

var fontOptions = [
  'Chivo', 'Work Sans', 'Inter', 'Poppins', 'Montserrat', 'Raleway',
  'Source Sans Pro', 'Nunito', 'Lato', 'Open Sans', 'DM Sans', 'Outfit'
];

export default function WhiteLabelSettings() {
  var [config, setConfig] = useState(null);
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);

  useEffect(function () {
    api.get('/whitelabel/config').then(function (r) { setConfig(r.data); })
      .catch(console.error).finally(function () { setLoading(false); });
  }, []);

  var save = async function () {
    setSaving(true);
    try {
      var resp = await api.put('/whitelabel/config', config);
      setConfig(resp.data);
      toast.success('Branding updated successfully');
    } catch (err) {
      toast.error('Failed to save');
    } finally { setSaving(false); }
  };

  var update = function (field, value) {
    setConfig(function (prev) { return Object.assign({}, prev, { [field]: value }); });
  };

  if (loading || !config) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="whitelabel-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Palette size={32} className="text-blue-400" weight="duotone" />
            White-label Settings
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Customize branding, colors, and typography</p>
        </div>
        <Button data-testid="save-whitelabel" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FloppyDisk size={16} />}
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">Brand Identity</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-zinc-300">Brand Name</Label>
              <Input data-testid="wl-brand-name" value={config.brand_name || ''} onChange={function (e) { update('brand_name', e.target.value); }} className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div>
              <Label className="text-zinc-300">Tagline</Label>
              <Input data-testid="wl-tagline" value={config.tagline || ''} onChange={function (e) { update('tagline', e.target.value); }} className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div>
              <Label className="text-zinc-300">Logo URL</Label>
              <Input data-testid="wl-logo-url" value={config.logo_url || ''} onChange={function (e) { update('logo_url', e.target.value); }} placeholder="https://..." className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
              {config.logo_url && <img src={config.logo_url} alt="Logo preview" className="mt-2 h-10 object-contain rounded" />}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">Colors</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-zinc-300">Primary Color</Label>
              <div className="flex gap-3 mt-1">
                <input type="color" data-testid="wl-primary-color" value={config.primary_color || '#3B82F6'} onChange={function (e) { update('primary_color', e.target.value); }} className="w-12 h-10 rounded border border-zinc-800 bg-zinc-950 cursor-pointer" />
                <Input value={config.primary_color || ''} onChange={function (e) { update('primary_color', e.target.value); }} className="flex-1 bg-zinc-950 border-zinc-800 text-zinc-100 font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300">Accent Color</Label>
              <div className="flex gap-3 mt-1">
                <input type="color" data-testid="wl-accent-color" value={config.accent_color || '#10B981'} onChange={function (e) { update('accent_color', e.target.value); }} className="w-12 h-10 rounded border border-zinc-800 bg-zinc-950 cursor-pointer" />
                <Input value={config.accent_color || ''} onChange={function (e) { update('accent_color', e.target.value); }} className="flex-1 bg-zinc-950 border-zinc-800 text-zinc-100 font-mono" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">Typography</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-zinc-300">Heading Font</Label>
              <select data-testid="wl-font-heading" value={config.font_heading || 'Chivo'} onChange={function (e) { update('font_heading', e.target.value); }} className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                {fontOptions.map(function (f) { return <option key={f} value={f}>{f}</option>; })}
              </select>
            </div>
            <div>
              <Label className="text-zinc-300">Body Font</Label>
              <select data-testid="wl-font-body" value={config.font_body || 'Work Sans'} onChange={function (e) { update('font_body', e.target.value); }} className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                {fontOptions.map(function (f) { return <option key={f} value={f}>{f}</option>; })}
              </select>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white flex items-center gap-2"><Eye size={18} className="text-blue-400" /> Live Preview</CardTitle></CardHeader>
          <CardContent>
            <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800">
              <div className="flex items-center gap-3 mb-4">
                {config.logo_url ? <img src={config.logo_url} alt="" className="h-8" /> : <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm text-white" style={{ backgroundColor: config.primary_color }}>{(config.brand_name || 'DW')[0]}</div>}
                <span className="font-bold text-lg text-white" style={{ fontFamily: config.font_heading }}>{config.brand_name}</span>
              </div>
              <p className="text-xs text-zinc-500 mb-3" style={{ fontFamily: config.font_body }}>{config.tagline}</p>
              <div className="flex gap-2">
                <div className="px-3 py-1.5 rounded-lg text-white text-xs font-medium" style={{ backgroundColor: config.primary_color }}>Primary Button</div>
                <div className="px-3 py-1.5 rounded-lg text-white text-xs font-medium" style={{ backgroundColor: config.accent_color }}>Accent Button</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
